/**
 * ENG-8201 (Slice 1): decide whether an agent is ACTUALLY rate-limited, from
 * Claude Code's own transcript rather than from pixels.
 *
 * Originally ENG-8198, where it lived in apps/cli as the manager's marker-arming
 * probe. It moved here because a SECOND consumer arrived: the channel MCP
 * servers now watch for a refusal AFTER dispatching (ENG-8201), instead of the
 * manager predicting one before. Two independent implementations of "was this
 * turn refused for the cap" would drift silently — the manager would arm on a
 * shape the MCP no longer recognises, or vice versa — and the failure mode of
 * this whole subsystem is silence, so drift would not announce itself. One
 * definition, two thin fs wrappers.
 *
 * ## Why the transcript and not the pane
 *
 * The predecessor gate was armed from banner text scraped out of `pane.log` — a
 * SCREEN CAPTURE, which cannot distinguish Claude Code's own status render from
 * the agent writing about the banner. On agt-aws-1 sherlock quoted the notice
 * while diagnosing another agent, the manager scraped its own agent's prose, and
 * sherlock went silent for hours behind a perfectly healthy account. ENG-8194
 * rejected the glued rendering; the same sentence with its spacing intact is
 * well-formed prose containing a well-formed banner, and no character-context
 * rule can separate those.
 *
 * Claude Code records the real thing. A turn refused for the cap lands in the
 * session transcript as a structured entry (captured verbatim from angie on the
 * DTI host, an account genuinely at its weekly limit):
 *
 *   { "type": "assistant",
 *     "timestamp": "2026-07-27T22:00:18.507Z",
 *     "message": { "model": "<synthetic>", "usage": { "input_tokens": 0, ... } },
 *     "error": "rate_limit", "isApiErrorMessage": true, "apiErrorStatus": 429,
 *     "content": [{ "type": "text", "text": "You've hit your weekly limit · resets 4pm (UTC)" }] }
 *
 * That is authoritative, machine-readable, and impossible for an agent to author
 * by talking about it.
 *
 * ## The shape trap
 *
 * Note the entry carefully: the refusal IS an assistant message and it DOES
 * carry a `usage` block (all zeros, model `<synthetic>`). An earlier cut of this
 * logic asked "has any assistant turn happened recently?" via a message count
 * over entries with a usage object — so every failed turn on a capped agent
 * would have counted as evidence of SERVING, the marker would never have armed,
 * and cap notices would have been silently dead fleet-wide. The feature would
 * have looked fine. Hence: classify entries explicitly, and key on the positive
 * rate-limit signal rather than on the absence of one.
 *
 * Pure by construction — a function of JSONL text and a time window, with no
 * `node:fs` and no path handling, so it stays browser-safe and each consumer
 * keeps its own reader (the manager enumerates the transcript dir; the MCP
 * watcher tails the files written since dispatch).
 */

import { parseUsageBanner } from './banner-parser.js';

/**
 * `capped`  — the newest classified turn in the window was refused for the cap.
 * `serving` — the newest classified turn completed normally, so the agent is not
 *             capped no matter what the pane says.
 * `unknown` — no classifiable turn (fresh agent, unreadable transcript, idle).
 */
export type RateLimitVerdict = 'capped' | 'serving' | 'unknown';

export interface RateLimitClassification {
  verdict: RateLimitVerdict;
  /** Epoch ms of the classified turn; null when `verdict` is `unknown`. */
  atMs: number | null;
  /**
   * For `capped`: the reset instant parsed out of the refusal's own text, or
   * null when the text carried none. This is the whole point of reporting the
   * refusal reactively — the reset time comes from the error Claude Code
   * returned, not from a banner scraped off a screen.
   */
  resetsAt: Date | null;
  /** For `capped`: the refusal text, for logging. Null otherwise. */
  text: string | null;
}

/** The "no classifiable turn" result. Shared so callers can compare identity-free. */
export const UNKNOWN_RATE_LIMIT: RateLimitClassification = Object.freeze({
  verdict: 'unknown',
  atMs: null,
  resetsAt: null,
  text: null,
});

/**
 * Concatenate the `text` parts of an assistant entry's content blocks. The
 * refusal carries its message as ordinary content, so this is where the reset
 * time comes from. Tolerates the content being absent, a bare string, or an
 * array of mixed block types.
 */
function contentText(record: Record<string, unknown>): string | null {
  // The captured refusal puts `content` at the TOP level of the entry, but
  // ordinary assistant turns put it under `message`. Accept either, so a shape
  // change in one place doesn't lose the reset time.
  const candidates: unknown[] = [record.content];
  const message = record.message;
  if (typeof message === 'object' && message !== null) {
    candidates.push((message as Record<string, unknown>).content);
  }
  const parts: string[] = [];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      if (candidate) parts.push(candidate);
      continue;
    }
    if (!Array.isArray(candidate)) continue;
    for (const block of candidate) {
      if (typeof block === 'string') {
        if (block) parts.push(block);
        continue;
      }
      if (typeof block !== 'object' || block === null) continue;
      const text = (block as { text?: unknown }).text;
      if (typeof text === 'string' && text) parts.push(text);
    }
  }
  const joined = parts.join('\n').trim();
  return joined ? joined : null;
}

/**
 * Classify one transcript line, or null when it carries no signal.
 *
 * Exported for tests and for a consumer that already has the lines split; most
 * callers want `classifyTranscriptRateLimit` over the whole file.
 */
export function classifyTranscriptLine(
  line: string,
  startMs: number,
  endMs: number,
  now?: Date,
): RateLimitClassification | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (typeof obj !== 'object' || obj === null) return null;
  const record = obj as Record<string, unknown>;
  if (record.type !== 'assistant') return null;

  const ts = record.timestamp;
  if (typeof ts !== 'string' || !ts) return null;
  const tsMs = new Date(ts).getTime();
  if (!Number.isFinite(tsMs) || tsMs < startMs || tsMs > endMs) return null;

  // The refusal: `error: "rate_limit"` with the 429 status. Both are checked so a
  // phrasing change in one field alone can't silently drop the signal.
  if (record.error === 'rate_limit' || record.apiErrorStatus === 429) {
    const text = contentText(record);
    // The refusal's text is the saturated banner form the parser already knows
    // ("You've hit your weekly limit · resets 4pm (UTC)"), so reuse it rather
    // than growing a second reset-time grammar that can drift from the first.
    const observation = text ? parseUsageBanner(text, now ?? new Date(endMs)) : null;
    return { verdict: 'capped', atMs: tsMs, resetsAt: observation?.weekResetsAt ?? null, text };
  }
  // Any other API error (500s, overloaded, network) says nothing about the cap —
  // don't let it read as a healthy turn.
  if (record.isApiErrorMessage === true) return null;

  const message = record.message;
  if (typeof message !== 'object' || message === null) return null;
  const msg = message as Record<string, unknown>;

  // A real completed turn: a genuine model (not the `<synthetic>` placeholder the
  // error path uses) that actually spent tokens.
  if (msg.model === '<synthetic>') return null;
  const usage = msg.usage;
  if (typeof usage !== 'object' || usage === null) return null;
  const u = usage as Record<string, unknown>;
  const spent =
    Number(u.input_tokens ?? 0) +
    Number(u.output_tokens ?? 0) +
    Number(u.cache_creation_input_tokens ?? 0) +
    Number(u.cache_read_input_tokens ?? 0);
  if (!Number.isFinite(spent) || spent <= 0) return null;

  return { verdict: 'serving', atMs: tsMs, resetsAt: null, text: null };
}

/**
 * Keep whichever classification is newer. `unknown` loses to anything classified,
 * and ties go to `next` so a later line in the same file wins (transcripts are
 * append-ordered, and two entries can share a timestamp to the millisecond).
 */
export function pickNewerClassification(
  current: RateLimitClassification,
  next: RateLimitClassification,
): RateLimitClassification {
  if (next.verdict === 'unknown') return current;
  if (current.verdict === 'unknown') return next;
  return next.atMs! >= current.atMs! ? next : current;
}

/**
 * Classify a whole transcript JSONL, returning the NEWEST classifiable turn in
 * `[startMs, endMs]`.
 *
 * Newest-wins is what makes the window safe to widen: a cap that has since
 * lifted shows a later successful turn and flips the verdict to `serving`
 * immediately, so a long window cannot pin a lifted cap open. Never throws — an
 * unparseable file is `unknown`.
 *
 * `now` only affects the year/date resolution of the reset time parsed out of a
 * refusal, and defaults to the end of the window.
 */
export function classifyTranscriptRateLimit(
  jsonl: string,
  startMs: number,
  endMs: number,
  now?: Date,
): RateLimitClassification {
  let newest: RateLimitClassification = UNKNOWN_RATE_LIMIT;
  for (const line of jsonl.split('\n')) {
    const classified = classifyTranscriptLine(line, startMs, endMs, now);
    if (classified) newest = pickNewerClassification(newest, classified);
  }
  return newest;
}
